import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/Card';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui/Feedback';
import { intensiveClassApi } from './intensiveClassApi';

interface PelitaIntensiveClassTabProps {
  individualId: string;
}

export function PelitaIntensiveClassTab({ individualId }: PelitaIntensiveClassTabProps) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['intensive-class', 'individual', individualId],
    queryFn: () => intensiveClassApi.getByIndividual(individualId),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-44 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => refetch()} />;
  }

  const record = data?.record;
  const sessions = data?.sessions ?? [];

  const attendedCount =
    record?.attendedSessions && Array.isArray(record.attendedSessions)
      ? record.attendedSessions.length
      : record
        ? Math.round((record.attendancePct / 100) * sessions.length)
        : 0;

  return (
    <div className="flex flex-col gap-6">
      {/* Hero Capaian Peserta */}
      {record ? (
        <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary-soft/30 via-white to-primary-soft/10">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border/60 pb-5">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xl">🎓</span>
                  <h3 className="text-lg font-bold text-ink">Rekam Jejak Kelas Intensif</h3>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                      record.status === 'lulus'
                        ? 'bg-emerald-100 text-emerald-800'
                        : record.status === 'mengikuti'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-amber-100 text-amber-800'
                    }`}
                  >
                    {record.status === 'lulus'
                      ? 'Lulus'
                      : record.status === 'mengikuti'
                        ? 'Mengikuti'
                        : 'Belum Selesai'}
                  </span>
                </div>
                <p className="text-xs text-ink-secondary">
                  Pelatihan tematik intensif pengembangan kapasitas Penggiat Literasi SLI.
                </p>
              </div>

              {/* Tombol E-Sertifikat */}
              {record.certificateKey ? (
                <a
                  href={`/api/v1/files/${record.certificateKey}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-[var(--radius-button)] bg-primary px-4 py-2.5 text-xs font-semibold text-white shadow-xs hover:bg-primary-hover transition cursor-pointer"
                >
                  <span>📜</span>
                  <span>Unduh E-Sertifikat</span>
                </a>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-subtle px-3 py-1.5 text-xs text-ink-muted">
                  <span>ℹ️</span> Sertifikat sedang diproses
                </span>
              )}
            </div>

            {/* Metrik Capaian Utama */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Presensi Kehadiran */}
              <div className="rounded-xl border border-border bg-white p-4 shadow-2xs">
                <div className="text-xs font-medium text-ink-secondary mb-1">Tingkat Kehadiran</div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-2xl font-extrabold text-ink tabular">
                    {record.attendancePct}%
                  </span>
                  <span className="text-xs text-ink-muted">
                    ({attendedCount} dari {sessions.length} Sesi Hadir)
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-subtle">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${Math.min(100, Math.max(0, record.attendancePct))}%` }}
                  />
                </div>
              </div>

              {/* Evaluasi Pre-Test & Post-Test */}
              <div className="rounded-xl border border-border bg-white p-4 shadow-2xs">
                <div className="text-xs font-medium text-ink-secondary mb-1">Evaluasi Pembelajaran</div>
                <div className="flex items-center gap-3 mt-1.5">
                  <div>
                    <div className="text-[10px] uppercase font-bold text-ink-muted">Pre-Test</div>
                    <div className="text-xl font-bold text-ink tabular">
                      {record.preTestScore !== null && record.preTestScore !== undefined
                        ? record.preTestScore
                        : '—'}
                    </div>
                  </div>
                  <span className="text-ink-muted text-base">➔</span>
                  <div>
                    <div className="text-[10px] uppercase font-bold text-primary">Post-Test</div>
                    <div className="text-xl font-extrabold text-primary tabular">
                      {record.postTestScore !== null && record.postTestScore !== undefined
                        ? record.postTestScore
                        : '—'}
                    </div>
                  </div>
                </div>
                <div className="text-[11px] text-ink-muted mt-2">Skala penilaian 0 – 100</div>
              </div>

              {/* Gain Score (Peningkatan) */}
              <div className="rounded-xl border border-border bg-white p-4 shadow-2xs">
                <div className="text-xs font-medium text-ink-secondary mb-1">Peningkatan (Gain Score)</div>
                <div className="flex items-baseline gap-2 mt-1.5">
                  <span
                    className={`text-2xl font-extrabold tabular ${
                      (record.gainScore ?? 0) > 0
                        ? 'text-emerald-600'
                        : (record.gainScore ?? 0) < 0
                          ? 'text-rose-600'
                          : 'text-ink'
                    }`}
                  >
                    {record.gainScore !== null && record.gainScore !== undefined
                      ? record.gainScore > 0
                        ? `+${record.gainScore}`
                        : `${record.gainScore}`
                      : '—'}
                  </span>
                  <span className="text-xs text-ink-muted">Poin</span>
                </div>
                <div className="text-[11px] text-ink-muted mt-2">
                  Selisih nilai post-test terhadap pre-test
                </div>
              </div>
            </div>

            {/* Catatan Fasilitator */}
            {record.notes && (
              <div className="rounded-lg bg-white/80 border border-border/80 p-3.5 text-xs">
                <span className="font-semibold text-ink">Catatan Fasilitator: </span>
                <span className="text-ink-secondary">{record.notes}</span>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card padded={false}>
          <EmptyState
            icon="🎓"
            title="Data capaian belum dicatat"
            description="Nilai pre-test, post-test, presensi, dan e-sertifikat Kelas Intensif akan ditampilkan di sini setelah diinput oleh Tim Fasilitator/Admin."
          />
        </Card>
      )}

      {/* Katalog Sesi Aktivitas */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-base font-bold text-ink">Kurikulum Sesi Kelas Intensif</h4>
            <p className="text-xs text-ink-secondary">
              Daftar tema materi, narasumber, tanggal pelaksanaan, dan status kehadiran individu di setiap kelas.
            </p>
          </div>
          <span className="text-xs font-medium text-ink-muted bg-subtle px-2.5 py-1 rounded-full border border-border">
            {sessions.length} Aktivitas
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          {sessions.map((s) => {
            const isAttended =
              record?.attendedSessions && Array.isArray(record.attendedSessions)
                ? record.attendedSessions.includes(s.sessionNumber)
                : record && record.attendancePct >= 100;

            const isAbsent =
              record?.attendedSessions && Array.isArray(record.attendedSessions)
                ? !record.attendedSessions.includes(s.sessionNumber)
                : false;

            return (
              <div
                key={s.id}
                className={`flex items-start gap-3.5 rounded-xl border p-4 shadow-2xs transition ${
                  isAttended
                    ? 'border-emerald-200 bg-emerald-50/20 hover:border-emerald-300'
                    : isAbsent
                      ? 'border-border/80 bg-subtle/20 hover:border-border'
                      : 'border-border bg-white hover:border-primary/40'
                }`}
              >
                <div
                  className={`flex size-9 shrink-0 items-center justify-center rounded-lg font-bold text-xs ${
                    isAttended
                      ? 'bg-emerald-100 text-emerald-800'
                      : isAbsent
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-primary-soft text-primary'
                  }`}
                >
                  {s.sessionNumber}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-ink leading-snug">{s.topic}</div>
                    {record && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider shrink-0 ${
                          isAttended
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                            : isAbsent
                              ? 'bg-rose-50 text-rose-700 border border-rose-200'
                              : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {isAttended ? 'Hadir ✅' : isAbsent ? 'Tidak Hadir ❌' : 'Tercatat'}
                      </span>
                    )}
                  </div>

                  <div className="mt-1 flex items-center gap-1.5 text-xs text-ink-secondary">
                    <span>🎙️</span>
                    <span className="font-medium text-ink">{s.speaker}</span>
                  </div>

                  {/* Evaluasi Pre & Post-Test Per Sesi */}
                  {(() => {
                    const sessionScore = record?.sessionScores?.find(
                      (sc) => sc.sessionNumber === s.sessionNumber,
                    );
                    if (!sessionScore) return null;
                    return (
                      <div className="mt-2.5 flex flex-wrap items-center gap-3 rounded-lg bg-white/90 px-3 py-2 border border-border/80 text-xs shadow-2xs">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold uppercase text-ink-muted">Pre:</span>
                          <span className="font-semibold text-ink tabular">
                            {sessionScore.preTest !== null && sessionScore.preTest !== undefined
                              ? sessionScore.preTest
                              : '—'}
                          </span>
                        </div>
                        <span className="text-ink-muted text-xs">➔</span>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[10px] font-bold uppercase text-primary">Post:</span>
                          <span className="font-extrabold text-primary tabular">
                            {sessionScore.postTest !== null && sessionScore.postTest !== undefined
                              ? sessionScore.postTest
                              : '—'}
                          </span>
                        </div>
                        <div className="ml-auto flex items-center gap-1">
                          <span className="text-[10px] text-ink-muted">Gain:</span>
                          <span
                            className={`font-extrabold text-[11px] px-1.5 py-0.5 rounded tabular ${
                              (sessionScore.gain ?? 0) > 0
                                ? 'bg-emerald-100 text-emerald-800'
                                : (sessionScore.gain ?? 0) < 0
                                  ? 'bg-rose-100 text-rose-800'
                                  : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {sessionScore.gain !== null && sessionScore.gain !== undefined
                              ? sessionScore.gain > 0
                                ? `+${sessionScore.gain}`
                                : `${sessionScore.gain}`
                              : '—'}{' '}
                            Poin
                          </span>
                        </div>
                      </div>
                    );
                  })()}

                  {s.date && (
                    <div className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-medium text-ink-secondary bg-white px-2 py-0.5 rounded border border-border/60">
                      <span>📅</span>
                      <span>
                        {new Date(s.date).toLocaleDateString('id-ID', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
